import React, { createContext, useMemo, useState, useEffect } from 'react'
import { CrearConfiguracionInicial, EditarConfiguracionInicial, EliminarConfiguracionInicial, GetConfigDetalleConfiguracionInicial, GetDatafonosByEds, GetEquiposByEds, GetListadoConfiguracionInicial, GetSurtidoresDetallesByEds } from '../services/ConfiguracionInicial.service'
import { CatalogoItem, ConfigInicialForm, ConfigInicialPayload, ConfigInicialRow, ConfiguracionInicialContextType, ESTADOS_CONFIG } from '../interfaces/ConfiguracionInicial.interfaces'
import useGenericInfo from 'hooks/useGenericInfo'

const emptyForm = (): ConfigInicialForm => ({
    id: null,
    identificador: '000000000000',
    organizacion_id: '',
    eds_id: '',
    equipo_id: '',
    estado_id: 1,
    numero_pantalla: null,
    permitir_efectivo: false,
    inactividad_segundos: 60,
    monto_minimo_programacion: 0,
    tiempo_espera_pago: 120,
    datafono_id: null,
    detalle_ids: [],
})

export const ConfiguracionInicialContext = createContext<ConfiguracionInicialContextType | undefined>(
    undefined
)

export const ConfiguracionInicialProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [rows, setRows] = useState<ConfigInicialRow[]>([])
    const [loadingTable, setLoadingTable] = useState(false)
    const [query, setQuery] = useState('')

    const [modalOpen, setModalOpen] = useState(false)
    const [step, setStep] = useState(1)
    const [form, setForm] = useState<ConfigInicialForm>(emptyForm())
    const [errors, setErrors] = useState<Record<string, string>>({})
    const [saving, setSaving] = useState(false)

    const [surtidoresDetalles, setSurtidoresDetalles] = useState<CatalogoItem[]>([])
    const [equipos, setEquipos] = useState<CatalogoItem[]>([])
    const [loadingEquipos, setLoadingEquipos] = useState(false)

    const [datafonos, setDatafonos] = useState<CatalogoItem[]>([])
    const [loadingDatafonos, setLoadingDatafonos] = useState(false)

    const [edsIdListado, setEdsIdListado] = useState<string | number | null>(null)

    const [loadingDetalle, setLoadingDetalle] = useState(false)

    const [readOnly, setReadOnly] = useState(false)

    const {
        organizationByUser: organizaciones,
        getEmpresasByOrganizacion,
        empresasByOrganizacion: edsOptions,
        loadingEmpresas: loadingEds,
    } = useGenericInfo({ organizations: true }) as any

    const filteredRows = useMemo(() => {
        const q = query.trim().toLowerCase()
        if (!q) return rows
        return rows.filter(
            (r) =>
                r.identificador.toLowerCase().includes(q) ||
                String(r.numero_pantalla).includes(q)
        )
    }, [rows, query])

    const setField = (key: keyof ConfigInicialForm, value: any) => {
        setForm((f) => ({ ...f, [key]: value }))
    }

    const onChangeOrganizacion = (orgId: any) => {
        setForm((f) => ({ ...f, organizacion_id: orgId ?? '', eds_id: '', equipo_id: '' }))
        setEquipos([])
        setSurtidoresDetalles([])
        if (orgId) getEmpresasByOrganizacion(orgId)
    }

    const onChangeEds = (edsId: any) => {
        setForm((f) => ({ ...f, eds_id: edsId ?? '', equipo_id: '', detalle_ids: [] }))
        setEquipos([])
        setSurtidoresDetalles([])
        if (!edsId) return

        setLoadingEquipos(true)
        GetEquiposByEds(edsId)
        .then((res: any) => {
            const lista = Array.isArray(res) ? res : res?.data ?? []
            const mapeados: CatalogoItem[] = lista
                .filter((eq: any) => eq.estado === 'A')
                .map((eq: any) => ({
                    id: eq.id,
                    nombre: eq.nombre ?? eq.identificador ?? `Equipo ${eq.id}`,
                }))
            setEquipos(mapeados)
        })
        .catch((err: any) => {
            console.error(err)
            setEquipos([])
        })
        .finally(() => setLoadingEquipos(false))
    }

    const openNew = () => {
        setReadOnly(false)
        setForm(emptyForm())
        setEquipos([])
        setSurtidoresDetalles([])
        setErrors({})
        setStep(1)
        setModalOpen(true)
    }

    const cargarDetalleConfiguracion = async (row: ConfigInicialRow) => {
        setErrors({})
        setStep(1)
        setModalOpen(true)
        setLoadingDetalle(true)

        try {
            const response = await GetConfigDetalleConfiguracionInicial(Number(row.id))

            if (!response.data?.success) {
                setErrors({ general: response.data?.message ?? 'No se pudo obtener el detalle de la configuración' })
                return
            }

            const { config, adicional, detalle_ids } = response.data.body

            setForm({
                ...emptyForm(),
                id: config.id,
                identificador: config.identificador.replace(/^POS/, ''),
                organizacion_id: config.organizacion_id ?? '',
                eds_id: config.estacion_id ?? '',
                equipo_id: config.equipo_id ?? '',
                estado_id: config.estado_id,
                numero_pantalla: config.numero_pantalla,
                permitir_efectivo: adicional?.permitir_efectivo ?? false,
                inactividad_segundos: adicional?.inactividad_segundos ?? 60,
                monto_minimo_programacion: adicional?.monto_minimo_programacion ?? 0,
                tiempo_espera_pago: adicional?.tiempo_espera_pago ?? 120,
                datafono_id: adicional?.datafono_id ?? null,
                detalle_ids: detalle_ids ?? [],
            })

            if (config.estacion_id) {
                setLoadingEquipos(true)
                GetEquiposByEds(config.estacion_id)
                    .then((res: any) => {
                        const lista = Array.isArray(res) ? res : res?.data ?? []
                        const mapeados: CatalogoItem[] = lista
                            .filter((eq: any) => eq.estado === 'A')
                            .map((eq: any) => ({
                                id: eq.id,
                                nombre: eq.nombre ?? eq.identificador ?? `Equipo ${eq.id}`,
                            }))
                        setEquipos(mapeados)
                    })
                    .catch((err: any) => {
                        console.error(err)
                        setEquipos([])
                    })
                    .finally(() => setLoadingEquipos(false))

                if (config.organizacion_id) getEmpresasByOrganizacion(config.organizacion_id)
            }
        } catch (error: any) {
            console.error(error)
            setErrors({ general: error.response?.data?.message ?? 'Error de conexión al obtener el detalle de la configuración' })
        } finally {
            setLoadingDetalle(false)
        }
    }

    const openEdit = async (row: ConfigInicialRow) => {
        setReadOnly(false)
        await cargarDetalleConfiguracion(row)
    }

    const openView = async (row: ConfigInicialRow) => {
        setReadOnly(true)
        await cargarDetalleConfiguracion(row)
    }

    const closeModal = () => setModalOpen(false)

    const validateStep = (currentStep: number) => {
        const e: Record<string, string> = {}
        if (currentStep === 1) {
            if (!form.identificador.trim()) e.identificador = 'Requerido'
            else if (form.identificador.length > 15) e.identificador = 'Máximo 15 caracteres'

            if (form.numero_pantalla == null) e.numero_pantalla = 'Requerido'

            const dupPantalla = rows.some(
                (r) => r.id !== form.id && String(r.numero_pantalla) === String(form.numero_pantalla)
            )
            if (dupPantalla) e.numero_pantalla = 'Ya está en uso'

            const dupId = rows.some(
                (r) => r.id !== form.id && r.identificador === 'POS' + form.identificador.trim()
            )
            if (dupId) e.identificador = 'Identificador duplicado'

            if (!form.organizacion_id) e.organizacion_id = 'Requerido'
            if (!form.eds_id) e.eds_id = 'Requerido'
            if (!form.equipo_id) e.equipo_id = 'Requerido'
        }

        if (currentStep === 3) {
            if (!form.detalle_ids.length) e.detalle_ids = 'Selecciona al menos un surtidor/manguera'
        }

        setErrors(e)
        return Object.keys(e).length === 0
    }

    const next = () => {
        if (validateStep(step)) setStep((s) => Math.min(3, s + 1))
    }

    const back = () => setStep((s) => Math.max(1, s - 1))

    const save = async (): Promise<{ success: boolean; message?: string }> => {
        if (!validateStep(1)) {
            setStep(1)
            return { success: false }
        }
        if (!validateStep(3)) {
            setStep(3)
            return { success: false }
        }

        const esEdicion = !!form.id

        try {
            setSaving(true)

            if (esEdicion) {
                const payloadEdicion = {
                    configuracion_id: Number(form.id),
                    numero_pantalla: Number(form.numero_pantalla),
                    estado_id: Number(form.estado_id),
                    permitir_efectivo: form.permitir_efectivo,
                    inactividad_segundos: Number(form.inactividad_segundos),
                    monto_minimo_programacion: Number(form.monto_minimo_programacion),
                    tiempo_espera_pago: Number(form.tiempo_espera_pago),
                    datafono_id: form.datafono_id ? Number(form.datafono_id) : null,
                    detalle_ids: form.detalle_ids ?? [],
                }

                const response = await EditarConfiguracionInicial(payloadEdicion)

                if (!response.data?.success) {
                    const msg = response.data?.message ?? 'No se pudo editar la configuración'
                    setErrors({ general: msg })
                    return { success: false, message: msg }
                }

                setModalOpen(false)
                fetchListado(edsIdListado)
                return { success: true, message: 'Configuración editada correctamente' }
            }

            const payload: ConfigInicialPayload = {
                identificador: 'POS' + form.identificador.trim(),
                numero_pantalla: Number(form.numero_pantalla),
                equipo_id: Number(form.equipo_id),
                estado_id: Number(form.estado_id),
                detalle_ids: form.detalle_ids,
                permitir_efectivo: form.permitir_efectivo,
                inactividad_segundos: Number(form.inactividad_segundos),
                monto_minimo_programacion: Number(form.monto_minimo_programacion),
                tiempo_espera_pago: Number(form.tiempo_espera_pago),
                datafono_id: form.datafono_id ? Number(form.datafono_id) : null,
            }

            const response = await CrearConfiguracionInicial(payload)

            if (!response.data?.success) {
                const msg = response.data?.message ?? 'No se pudo crear la configuración'
                setErrors({ general: msg })
                return { success: false, message: msg }
            }

            setModalOpen(false)
            fetchListado(edsIdListado)
            return { success: true }
        } catch (error: any) {
            console.error(error)
            const msg = error.response?.data?.message ?? (esEdicion ? 'Hubo un error al editar la configuración.' : 'Hubo un error al crear la configuración.')
            setErrors({ general: msg })
            return { success: false, message: msg }
        } finally {
            setSaving(false)
        }
    }

    const toggleDetalle = (id: number) => {
        setForm((f) => {
            const idNum = Number(id)
            const yaEsta = f.detalle_ids.map(Number).includes(idNum)
            return {
                ...f,
                detalle_ids: yaEsta
                    ? f.detalle_ids.filter((d) => Number(d) !== idNum)
                    : [...f.detalle_ids, idNum],
            }
        })
    }

    const isDetalleSeleccionado = (id: number) => {
        console.log({id, formdetalles: form.detalle_ids})
        return form.detalle_ids.map(Number).includes(Number(id))
    }

    const fetchListado = (edsId: string | number | null) => {
        if (!edsId) {
            setRows([])
            setEdsIdListado(null)
            return
        }
        setEdsIdListado(edsId)
        setLoadingTable(true)
        GetListadoConfiguracionInicial(edsId)
            .then((response: any) => {
                if (!response.data?.success) {
                    setRows([])
                    return
                }
                setRows(response.data.body ?? [])
            })
            .catch((err: any) => {
                console.error(err)
                setRows([])
            })
            .finally(() => setLoadingTable(false))
    }

    const toggleEstadoConfiguracion = async (record: ConfigInicialForm): Promise<{ success: boolean; message?: string }> => {
        const nuevoEstado = record.estado_id === ESTADOS_CONFIG.INACTIVO ? ESTADOS_CONFIG.ACTIVO : ESTADOS_CONFIG.INACTIVO

        try {
            const response = await EliminarConfiguracionInicial({
                configuracion_id: Number(record.id),
                estado_inactivo_id: nuevoEstado,
            })

            if (!response.data?.success) {
                return { success: false, message: response.data?.message ?? 'No se pudo actualizar el estado de la configuración' }
            }

            fetchListado(edsIdListado)
            return {
                success: true,
                message: nuevoEstado === ESTADOS_CONFIG.INACTIVO ? 'Configuración inactivada correctamente' : 'Configuración activada correctamente',
            }
        } catch (error: any) {
            console.error(error)
            return { success: false, message: error.response?.data?.message ?? 'Error de conexión al actualizar la configuración' }
        }
    }

    useEffect(() => {
        if (step !== 3) return
        if (!form.eds_id || !form.equipo_id) return

        GetSurtidoresDetallesByEds(+form.eds_id)
            .then((response: any) => {
                if (!response.data.success) {
                    setSurtidoresDetalles([])
                    return
                }
                const detalles: CatalogoItem[] = response.data.mensaje.flatMap((isla: any) =>
                    isla.surtidores
                        .filter((surtidor: any) => Number(surtidor.equipos_id) === Number(form.equipo_id))
                        .flatMap((surtidor: any) =>
                            surtidor.surtidoresdetalles.map((detalle: any) => ({
                                id: detalle.id,
                                nombre: `Surtidor ${detalle.surtidor} · Cara ${detalle.cara} · Manguera ${detalle.manguera}`,
                            }))
                        )
                )
                setSurtidoresDetalles(detalles)
            })
            .catch((err: any) => {
                console.error(err)
                setSurtidoresDetalles([])
            })
    }, [step, form.eds_id, form.equipo_id])

    useEffect(() => {
        if (step !== 2) return
        if (!form.eds_id) return

        setLoadingDatafonos(true)
        GetDatafonosByEds(form.eds_id.toString())
            .then((res: any) => {
                const lista = Array.isArray(res) ? res : res?.data ?? []
                const mapeados: CatalogoItem[] = lista.map((d: any) => ({
                    id: Number(d.id_datafono),
                    nombre: d.descripcion,
                }))
                setDatafonos(mapeados)
            })
            .catch((err: any) => {
                console.error(err)
                setDatafonos([])
            })
            .finally(() => setLoadingDatafonos(false))
    }, [step, form.eds_id])

    const value: ConfiguracionInicialContextType = {
        rows,
        loadingTable,
        query,
        setQuery,
        filteredRows,
        datafonos,
        surtidoresDetalles,
        organizaciones: organizaciones ?? [],
        edsOptions: edsOptions ?? [],
        equipos,
        loadingEds: !!loadingEds,
        loadingEquipos,
        onChangeOrganizacion,
        onChangeEds,
        modalOpen,
        step,
        form,
        errors,
        saving,
        openNew,
        openEdit,
        closeModal,
        setField,
        next,
        back,
        save,
        toggleDetalle,
        isDetalleSeleccionado,
        fetchListado,
        toggleEstadoConfiguracion,
        loadingDetalle,
        readOnly,
        openView
    }

    return (
        <ConfiguracionInicialContext.Provider value={value}>
            {children}
        </ConfiguracionInicialContext.Provider>
    )
}